/**
 * Per-session git worktrees under a configurable worktrees root (default ~/.buildautomaton/worktrees).
 * Layout: `{worktreesRoot}/{bridgeKey}/{sessionId}/{repoRel…}` — each repo directory under the session folder is
 * a git worktree mirroring the bridge. **Session parent path** for `worktrees_root` is that `{bridgeKey}/{sessionId}`
 * directory (agent cwd, UI), never the global worktrees root or the bridge-key folder alone.
 */

import type { WorkingTreeChangeRepoDetail } from '../../git/changes/index.js';
import type { SessionWorktreeManagerOptions } from './types.js';
import {
  createSessionWorktreeManagerContext,
  type SessionWorktreeManagerContext,
} from './session-worktree-manager-context.js';
import { createSessionWorktreeGitApi } from './session-worktree-git.js';
import { createSessionWorktreePathsApi } from './session-worktree-paths.js';
import { resolveCommitTargetsAsync } from './resolve-commit-targets.js';
import type {
  SessionWorkingTreeChangeDetailsOpts,
  SessionWorkingTreeChangeFilePatchOpts,
} from './session-worktree-manager-types.js';

export type {
  ResolveSessionParentPathForPromptOpts,
  SessionWorkingTreeChangeDetailsOpts,
  SessionWorkingTreeChangeFilePatchOpts,
} from './session-worktree-manager-types.js';

type SessionWorktreePathsApi = ReturnType<typeof createSessionWorktreePathsApi>;
type SessionWorktreeGitApi = ReturnType<typeof createSessionWorktreeGitApi>;

export interface SessionWorktreeManager extends SessionWorktreePathsApi, SessionWorktreeGitApi {
  readonly worktreesRootPath: string;
  resolveSessionRepoCheckoutPaths(sessionId: string): Promise<string[]>;
  getSessionParentPath(sessionId: string): string | undefined;
  getSessionWorkingTreeChangeDetails(
    sessionId: string,
    opts?: SessionWorkingTreeChangeDetailsOpts,
  ): Promise<WorkingTreeChangeRepoDetail[]>;
  getSessionWorkingTreeChangeFilePatch(
    sessionId: string,
    opts: SessionWorkingTreeChangeFilePatchOpts,
  ): Promise<{ patchContent?: string; totalLines: number | null }>;
}

export class SessionWorktreeManager implements SessionWorktreeManager {
  readonly worktreesRootPath: string;
  private readonly ctx: SessionWorktreeManagerContext;

  constructor(options: SessionWorktreeManagerOptions) {
    this.ctx = createSessionWorktreeManagerContext(options);
    this.worktreesRootPath = this.ctx.worktreesRootPath;
    Object.assign(this, createSessionWorktreePathsApi(this.ctx), createSessionWorktreeGitApi(this.ctx));
  }

  async resolveSessionRepoCheckoutPaths(sessionId: string): Promise<string[]> {
    return resolveCommitTargetsAsync(sessionId, this.ctx.cache, (sid) => this.discoverAsync(sid));
  }

  getSessionParentPath(sessionId: string): string | undefined {
    return this.ctx.cache.getSessionParentPath(sessionId);
  }
}
